/**
 * Site Config Hooks
 *
 * React Query hooks for site configuration management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { siteApi } from '../api/easelApi'
import { useToast } from '@/hooks/use-toast'
import type { UpdateSiteConfigData } from '../types'

// =============================================================================
// Query Keys
// =============================================================================

export const siteKeys = {
  all: ['site'] as const,
  config: () => [...siteKeys.all, 'config'] as const,
}

// =============================================================================
// Site Config Hooks
// =============================================================================

export function useSiteConfig() {
  return useQuery({
    queryKey: siteKeys.config(),
    queryFn: siteApi.getConfig,
  })
}

export function useUpdateSiteConfig() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: UpdateSiteConfigData) => siteApi.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.config() })
      toast({
        title: 'Settings Saved',
        description: 'Your site configuration has been updated.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update settings',
        variant: 'destructive',
      })
    },
  })
}

export function useInitializeSiteConfig() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: siteApi.initialize,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.all })
      toast({
        title: 'Site Initialized',
        description: 'Your site configuration has been created.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize site',
        variant: 'destructive',
      })
    },
  })
}
